import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { CheckboxField, Text } from '@frontend/design-system';
import { AxisGroup, AxisOption } from './types';

const CheckboxOption = ({
  option,
  checked,
  indeterminate,
  disabled,
  onChange,
  isChild,
}: {
  option: AxisOption;
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  isChild?: boolean;
}) => {
  const dummyProps = {
    id: `checkbox-${option.value || option.label}`,
    onBlur: () => {},
    onFocus: () => {},
    error: '',
    touched: false,
    'aria-invalid': false,
    active: false,
  };

  return (
    <div css={[styles.optionRow, isChild && styles.childOption]}>
      <CheckboxField
        {...dummyProps}
        name={option.value || option.label}
        value={checked || indeterminate}
        indeterminate={indeterminate}
        disabled={disabled}
        onChange={(e: any) => {
          const val = typeof e === 'boolean' ? e : e && typeof e.value === 'boolean' ? e.value : !!e?.target?.checked;
          onChange(val);
        }}
        label={<Text size='large'>{option.label}</Text>}
      />
    </div>
  );
};

export const CheckboxGroup = ({
  group,
  draftY,
  onCheckboxChange,
  disabledOptions = [],
}: {
  group: AxisGroup;
  draftY: string[];
  onCheckboxChange: (values: string[]) => void;
  disabledOptions?: string[];
}) => {
  const getChildValues = (option: AxisOption): string[] => {
    if (!option.children) return [];
    return option.children.reduce<string[]>((acc, child) => {
      acc.push(child.value);
      acc.push(...getChildValues(child));
      return acc;
    }, []);
  };

  const handleOptionChange = (option: AxisOption, valOrEvent: any) => {
    const checked = typeof valOrEvent === 'boolean' ? valOrEvent : !!valOrEvent?.target?.checked;
    let newDraftY = [...draftY];

    if (option.children && option.children.length > 0) {
      const childValues = getChildValues(option);
      if (checked) {
        // Check all children
        childValues.forEach((v) => {
          if (!newDraftY.includes(v)) {
            newDraftY.push(v);
          }
        });
      } else {
        // Uncheck all children
        newDraftY = newDraftY.filter((v) => !childValues.includes(v));
      }
    } else {
      if (checked) {
        if (!newDraftY.includes(option.value)) {
          newDraftY.push(option.value);
        }
      } else {
        newDraftY = newDraftY.filter((v) => v !== option.value);
      }
    }

    onCheckboxChange(newDraftY);
  };

  const renderOption = (option: AxisOption, isChild = false) => {
    const hasChildren = option.children && option.children.length > 0;
    let checked = false;
    let indeterminate = false;

    if (hasChildren) {
      const childValues = getChildValues(option);
      const selectedChildrenCount = childValues.filter((v) => draftY.includes(v)).length;
      checked = selectedChildrenCount === childValues.length && childValues.length > 0;
      indeterminate = selectedChildrenCount > 0 && selectedChildrenCount < childValues.length;
    } else {
      checked = draftY.includes(option.value);
    }

    return (
      <CheckboxOption
        key={option.value || option.label}
        option={option}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabledOptions.includes(option.value)}
        onChange={(val) => handleOptionChange(option, val)}
        isChild={isChild}
      />
    );
  };

  return (
    <div css={styles.checklistField}>
      {group.options.map((option) => (
        <div key={option.value || option.label}>
          {renderOption(option)}
          {option.children && option.children.map((child) => renderOption(child, true))}
        </div>
      ))}
    </div>
  );
};

const styles = {
  checklistField: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  }),
  optionRow: css({
    display: 'flex',
    alignItems: 'center',
  }),
  childOption: css({
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
  }),
};
